import React, { useEffect, useRef, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  Switch } from 'react-native'
import { X, Check, AlertTriangle, ImagePlus } from 'lucide-react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { CameraView, useCameraPermissions } from 'expo-camera'
import * as ImagePicker from 'expo-image-picker'
import * as FileSystem from 'expo-file-system/legacy'
import { Platform } from 'react-native'
import { supabase } from '../lib/supabase'
import { getProductSuggestions } from '../lib/openrouter'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../components/Toast'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  Button,
  ButtonText,
  Card,
  Spinner,
  XStack,
  YStack } from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Camera'>
  route: RouteProp<RootStackParamList, 'Camera'>
}

interface UploadStatus {
  productId: string
  status: 'uploading' | 'success' | 'error'
  progress?: number
}

export default function CameraScreen({ navigation, route }: Props) {
  const { user } = useAuth()
  const { t } = useLanguage()
  const toast = useToast()
  const reservationId = route.params?.reservationId
  const pickFromGallery = route.params?.pickFromGallery
  const cameraRef = useRef<CameraView>(null)
  const [permission, requestPermission] = useCameraPermissions()
  const [batchId, setBatchId] = useState<string | null>(null)
  const [photoCount, setPhotoCount] = useState(0)
  const [uploadStatuses, setUploadStatuses] = useState<UploadStatus[]>([])
  const [isCapturing, setIsCapturing] = useState(false)
  const [autoLabel, setAutoLabel] = useState(true)

  // Load auto-label preference
  useEffect(() => {
    AsyncStorage.getItem('autoLabel').then((value) => {
      if (value !== null) {
        setAutoLabel(value === 'true')
      }
    })
  }, [])

  // Save auto-label preference when changed
  const toggleAutoLabel = async (value: boolean) => {
    setAutoLabel(value)
    await AsyncStorage.setItem('autoLabel', value.toString())
  }

  // Start upload batch when camera opens
  useEffect(() => {
    startBatch()
    return () => {
      // Optionally close batch when leaving camera
    }
  }, [])

  const startBatch = async () => {
    try {
      const { data, error } = await supabase.rpc('create_upload_batch', {
        p_seller_id: user?.id,
        p_market_id: null, // Can be set if seller has selected a market
      })
      if (error) throw error
      setBatchId(data)
    } catch (error) {
      console.error('Error creating batch:', error)
      // Continue without batch - products will be created individually
    }
  }

  const takePicture = async () => {
    if (!cameraRef.current || isCapturing) return

    setIsCapturing(true)
    try {
      const photo = await cameraRef.current.takePictureAsync({
        quality: 0.8,
        skipProcessing: true })

      if (!photo?.uri) throw new Error('Failed to capture photo')

      // Create product in database immediately
      const { data: productData, error: productError } = await supabase.rpc(
        'create_product_from_upload',
        {
          p_seller_id: user?.id,
          p_batch_id: batchId }
      )

      if (productError) throw productError

      // Function returns a table, so data is an array
      const row = Array.isArray(productData) ? productData[0] : productData
      const productId = row.product_id
      const imageId = row.image_id

      setPhotoCount((prev) => prev + 1)
      setUploadStatuses((prev) => [
        { productId, status: 'uploading' },
        ...prev,
      ])

      // Upload in background (and run AI labeling in parallel if enabled)
      uploadImageAndLabel(productId, imageId, photo.uri)
    } catch (error: any) {
      console.error('Error capturing:', error)
      toast.error('Virhe', error.message)
    } finally {
      setIsCapturing(false)
    }
  }

  const uploadImageAndLabel = async (productId: string, imageId: string, uri: string) => {
    try {
      let fileData: Blob | Uint8Array

      if (Platform.OS === 'web') {
        // On web, fetch the blob URL directly
        const response = await fetch(uri)
        fileData = await response.blob()
      } else {
        // On native, read file as base64
        const base64 = await FileSystem.readAsStringAsync(uri, {
          encoding: 'base64' })
        fileData = decode(base64)
      }

      // Upload to Supabase Storage
      const fileName = `${user?.id}/${productId}/${imageId}.jpg`
      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(fileName, fileData, {
          contentType: 'image/jpeg',
          upsert: true })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(fileName)

      const publicUrl = urlData.publicUrl

      // Update product_images record with URL
      const { error: updateError } = await supabase
        .from('product_images')
        .update({
          image_url: publicUrl,
          upload_status: 'completed' })
        .eq('id', imageId)

      if (updateError) throw updateError

      // Run AI labeling and DB update in parallel
      // Also auto-link to reservation if reservationId is provided
      const aiLabelPromise = autoLabel
        ? getProductSuggestions(publicUrl)
            .then(async (suggestion) => {
              const barcode = `P${Date.now().toString(36).toUpperCase()}`
              const updateData: Record<string, any> = {
                title: suggestion.title_en || '',
                title_en: suggestion.title_en || '',
                title_fi: suggestion.title_fi || '',
                description: suggestion.description_en || '',
                description_en: suggestion.description_en || '',
                description_fi: suggestion.description_fi || '',
                price: suggestion.price || 1,
                barcode: barcode,
                status: reservationId ? 'for_sale' : 'draft' }
              // Auto-link to reservation if provided
              if (reservationId) {
                updateData.reservation_id = reservationId
              }
              await supabase
                .from('products')
                .update(updateData)
                .eq('id', productId)
            })
            .catch((err) => {
              console.error('AI labeling error:', err)
              // Still update status and link to reservation even if AI fails
              const updateData: Record<string, any> = {
                status: reservationId ? 'for_sale' : 'draft'
              }
              if (reservationId) {
                updateData.reservation_id = reservationId
              }
              return supabase
                .from('products')
                .update(updateData)
                .eq('id', productId)
            })
        : supabase
            .from('products')
            .update({
              status: reservationId ? 'for_sale' : 'draft',
              ...(reservationId ? { reservation_id: reservationId } : {})
            })
            .eq('id', productId)

      await aiLabelPromise

      // Update local status
      setUploadStatuses((prev) =>
        prev.map((s) =>
          s.productId === productId ? { ...s, status: 'success' } : s
        )
      )
    } catch (error) {
      console.error('Upload error:', error)
      setUploadStatuses((prev) =>
        prev.map((s) =>
          s.productId === productId ? { ...s, status: 'error' } : s
        )
      )
    }
  }

  // Helper to decode base64 to Uint8Array
  const decode = (base64: string): Uint8Array => {
    const binaryString = atob(base64)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes
  }

  const finishSession = () => {
    navigation.goBack()
  }

  const pickFromLibrary = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsMultipleSelection: true,
        quality: 0.8 })

      if (result.canceled || !result.assets || result.assets.length === 0) {
        // If launched in gallery-only mode and user cancels, go back
        if (pickFromGallery) {
          navigation.goBack()
        }
        return
      }

      // Process each selected image
      for (const asset of result.assets) {
        await processImage(asset.uri)
      }

      // If launched in gallery-only mode, go back after processing
      if (pickFromGallery) {
        navigation.goBack()
      }
    } catch (error: any) {
      console.error('Error picking image:', error)
      toast.error('Virhe', error.message)
      // Go back on error if in gallery-only mode
      if (pickFromGallery) {
        navigation.goBack()
      }
    }
  }

  const processImage = async (uri: string) => {
    try {
      // Create product in database immediately
      const { data: productData, error: productError } = await supabase.rpc(
        'create_product_from_upload',
        {
          p_seller_id: user?.id,
          p_batch_id: batchId }
      )

      if (productError) throw productError

      // Function returns a table, so data is an array
      const row = Array.isArray(productData) ? productData[0] : productData
      const productId = row.product_id
      const imageId = row.image_id

      setPhotoCount((prev) => prev + 1)
      setUploadStatuses((prev) => [
        { productId, status: 'uploading' },
        ...prev,
      ])

      // Upload in background (and run AI labeling in parallel if enabled)
      uploadImageAndLabel(productId, imageId, uri)
    } catch (error: any) {
      console.error('Error processing image:', error)
      toast.error('Virhe', error.message)
    }
  }

  // Auto-open gallery picker if pickFromGallery param is set
  // Gallery doesn't need camera permission, so open immediately
  useEffect(() => {
    if (pickFromGallery) {
      pickFromLibrary()
    }
  }, [pickFromGallery])

  // If in gallery-only mode, show loading while picker opens
  // Don't block on camera permission for gallery
  if (pickFromGallery) {
    return (
      <View style={styles.container}>
        <Spinner size="large" />
        <Text style={[styles.permissionText, { marginTop: 16 }]}>
          {t('opening_gallery')}
        </Text>
      </View>
    )
  }

  if (!permission) {
    return (
      <View style={styles.container}>
        <Spinner size="large" />
      </View>
    )
  }

  if (!permission.granted) {
    return (
      <View style={styles.container}>
        <Text style={styles.permissionText}>
          {t('camera_permission')}
        </Text>
        <TouchableOpacity style={styles.permissionButton} onPress={requestPermission}>
          <Text style={styles.permissionButtonText}>{t('grant_permission')}</Text>
        </TouchableOpacity>
      </View>
    )
  }

  const uploadingCount = uploadStatuses.filter((s) => s.status === 'uploading').length
  const successCount = uploadStatuses.filter((s) => s.status === 'success').length
  const errorCount = uploadStatuses.filter((s) => s.status === 'error').length

  return (
    <View style={styles.container}>
      <CameraView
        ref={cameraRef}
        style={styles.camera}
        facing="back"
      >
        {/* Top status bar */}
        <View style={styles.statusBar}>
          <TouchableOpacity style={styles.closeButton} onPress={finishSession}>
            <X size={24} color="#fff" />
          </TouchableOpacity>

          <View style={styles.autoLabelToggle}>
            <Text style={styles.autoLabelText}>{t('ai_label')}</Text>
            <Switch
              value={autoLabel}
              onValueChange={toggleAutoLabel}
              trackColor={{ false: '#767577', true: '#9C27B0' }}
              thumbColor={autoLabel ? '#E1BEE7' : '#f4f3f4'}
            />
          </View>

          <View style={styles.counters}>
            <View style={styles.counter}>
              <Text style={styles.counterValue}>{photoCount}</Text>
              <Text style={styles.counterLabel}>{t('captured')}</Text>
            </View>
            {uploadingCount > 0 && (
              <View style={[styles.counter, styles.uploadingCounter]}>
                <Spinner size="small" />
                <Text style={styles.counterLabel}>{uploadingCount} {t('uploading')}</Text>
              </View>
            )}
            {successCount > 0 && (
              <View style={[styles.counter, styles.successCounter]}>
                <Check size={14} color="#fff" />
                <Text style={styles.counterValue}>{successCount}</Text>
              </View>
            )}
            {errorCount > 0 && (
              <View style={[styles.counter, styles.errorCounter]}>
                <AlertTriangle size={14} color="#fff" />
                <Text style={styles.counterValue}>{errorCount}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Capture button */}
        <View style={styles.bottomBar}>
          <TouchableOpacity style={styles.galleryButton} onPress={pickFromLibrary}>
            <ImagePlus size={28} color="#fff" />
          </TouchableOpacity>
          <View style={styles.captureButtonOuter}>
            <TouchableOpacity
              style={[
                styles.captureButton,
                isCapturing && styles.captureButtonDisabled,
              ]}
              onPress={takePicture}
              disabled={isCapturing}
            >
              {isCapturing && (
                <Spinner color="#4A90A4" style={styles.captureSpinner} />
              )}
            </TouchableOpacity>
          </View>
          <View style={styles.galleryButtonPlaceholder} />
        </View>
      </CameraView>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center' },
  camera: {
    flex: 1,
    width: '100%' },
  statusBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingTop: 16,
    paddingHorizontal: 16 },
  closeButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center' },
  closeButtonText: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold' },
  autoLabelToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    gap: 8 },
  autoLabelText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600' },
  counters: {
    flexDirection: 'row',
    gap: 8 },
  counter: {
    backgroundColor: 'rgba(0,0,0,0.5)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    alignItems: 'center',
    flexDirection: 'row',
    gap: 6 },
  counterValue: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold' },
  counterLabel: {
    color: '#fff',
    fontSize: 12,
    opacity: 0.8 },
  uploadingCounter: {
    backgroundColor: 'rgba(255,152,0,0.8)' },
  successCounter: {
    backgroundColor: 'rgba(76,175,80,0.8)' },
  errorCounter: {
    backgroundColor: 'rgba(244,67,54,0.8)' },
  bottomBar: {
    position: 'absolute',
    bottom: 40,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40 },
  galleryButton: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 24 },
  galleryButtonPlaceholder: {
    width: 56,
    height: 56,
    marginLeft: 24 },
  captureButtonOuter: {
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 4,
    borderColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center' },
  captureButton: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center' },
  captureButtonDisabled: {
    backgroundColor: '#ccc' },
  captureSpinner: {
    position: 'absolute' },
  permissionText: {
    color: '#fff',
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 16,
    paddingHorizontal: 32 },
  permissionButton: {
    backgroundColor: '#4A90A4',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8 },
  permissionButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' } })
